import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Card, CardTitle, CardBody, Button, Table } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../constants/ApiService';
import VendorBranchSelector from '../../components/VendorBranchSelector';
import { useSelector } from 'react-redux';
import conditionalDataLoad from '../../utils/conditionalDataLoad';
import useVendorBranch from '../../utils/useVendorBranch';
import {
  createAppConnection,
  joinModuleGroup,
  leaveModuleGroup,
  subscribeRealtimeUpdate,
  unsubscribeRealtimeUpdate,
} from '../../utils/signalRService';

const MODULE_NAME = 'SalaryDefinition';

const SalaryDefinitionList = () => {
  const { user } = useSelector((state) => state.authState);
  const navigate = useNavigate();

  const { isSuperAdmin, isAdmin, hasMultiBranch, hasSingleBranch } =
    useVendorBranch(user);

  const [errorMessage, setErrorMessage] = useState({});
  const [salaryDefinitionList, setSalaryDefinitionList] = useState([]);

  const [selectionData, setSelectionData] = useState({
    vendor_id: '',
    branch_id: '',
  });

  const currentGroupRef = useRef({
    module: null,
    vendorId: null,
    branchId: null,
  });

  const onHandleAdd = () => {
    navigate('/save-salary-definition');
  };

  const onHandleEdit = (id) => {
    const selected = salaryDefinitionList.find((x) => x.id === id);
    navigate('/save-salary-definition', { state: { ...(selected || {}) } });
  };

  const GetSalaryDefinitionList = useCallback(async () => {
    try {
      const vendorId = selectionData.vendor_id || '';
      const branchId = selectionData.branch_id || '';

      const res = await apiService.get(
        `api/SalaryDefinition/GetAllSalaryDefinition?vendorId=${vendorId}&branchId=${branchId}`,
      );

      console.log('GET SALARY DEFINITION RESPONSE:', res);

      if (res?.status === true) {
        setSalaryDefinitionList(res.data || res.Data || []);
      } else {
        alert(res?.message || res?.Message || 'Failed');
      }
    } catch (e) {
      console.error(e);
      alert('Something went wrong');
    }
  }, [selectionData.vendor_id, selectionData.branch_id]);

  useEffect(() => {
    const canLoad = conditionalDataLoad({
      hasMultiBranch,
      hasSingleBranch,
      isSuperAdmin,
      isAdmin,
      vendorId: selectionData.vendor_id,
      branchId: selectionData.branch_id,
    });

    if (canLoad) {
      GetSalaryDefinitionList();
    } else {
      setSalaryDefinitionList([]);
    }
  }, [
    selectionData.vendor_id,
    selectionData.branch_id,
    hasMultiBranch,
    hasSingleBranch,
    isSuperAdmin,
    isAdmin,
    GetSalaryDefinitionList,
  ]);

  useEffect(() => {
    let mounted = true;

    const setupSignalR = async () => {
      try {
        await createAppConnection();

        const vendorId = selectionData.vendor_id || 0;
        const branchId = selectionData.branch_id || 0;

        const prev = currentGroupRef.current;

        if (
          prev.vendorId &&
          prev.branchId &&
          (String(prev.vendorId) !== String(vendorId) ||
            String(prev.branchId) !== String(branchId))
        ) {
          await leaveModuleGroup(prev.module, prev.vendorId, prev.branchId);
        }

        if (vendorId && branchId) {
          await joinModuleGroup(MODULE_NAME, vendorId, branchId);

          currentGroupRef.current = {
            module: MODULE_NAME,
            vendorId,
            branchId,
          };
        }

        unsubscribeRealtimeUpdate();

        await subscribeRealtimeUpdate((payload) => {
          if (!mounted) return;
          if (!payload) return;

          const isSameModule = String(payload.module || '') === MODULE_NAME;
          const isSameVendor =
            String(payload.vendorId || '') === String(vendorId || '');
          const isSameBranch =
            String(payload.branchId || '') === String(branchId || '');

          if (isSameModule && isSameVendor && isSameBranch) {
            console.log('SalaryDefinition realtime update received:', payload);
            GetSalaryDefinitionList();
          }
        });
      } catch (err) {
        console.error('SignalR error:', err);
      }
    };

    const canLoad = conditionalDataLoad({
      hasMultiBranch,
      hasSingleBranch,
      isSuperAdmin,
      isAdmin,
      vendorId: selectionData.vendor_id,
      branchId: selectionData.branch_id,
    });

    if (canLoad) {
      setupSignalR();
    }

    return () => {
      mounted = false;
    };
  }, [
    selectionData.vendor_id,
    selectionData.branch_id,
    hasMultiBranch,
    hasSingleBranch,
    isSuperAdmin,
    isAdmin,
    GetSalaryDefinitionList,
  ]);

  useEffect(() => {
    return () => {
      unsubscribeRealtimeUpdate();

      const prev = currentGroupRef.current;
      if (prev?.vendorId && prev?.branchId) {
        leaveModuleGroup(prev.module, prev.vendorId, prev.branchId).catch(
          console.error,
        );
      }
    };
  }, []);

  return (
    <>
      <div className='text-right mb-2'>
        <Button color='primary' onClick={onHandleAdd}>
          <i className='bi bi-plus-circle' /> Add Salary Definition
        </Button>
      </div>

      {/* SAME WEEKEND WALA VENDOR / BRANCH DROPDOWN */}
      <VendorBranchSelector
        user={user}
        selectionData={selectionData}
        setSelectionData={setSelectionData}
        errorMessage={errorMessage}
        accessButton={true}
      />

      <Card className='card-box pd-20 mb-20'>
        <CardTitle className='p-3 mb-0'>
          <i className='bi bi-cash-stack mr-2' /> Salary Definition
        </CardTitle>

        <CardBody>
          <Table striped hover responsive>
            <thead>
              <tr>
                <th>#</th>
                <th>Edit</th>
                <th>Designation</th>
                <th>Salary</th>
                <th>Leave Allowed</th>
              </tr>
            </thead>

            <tbody>
              {salaryDefinitionList.length > 0 ? (
                salaryDefinitionList.map((x, i) => (
                  <tr key={x.id}>
                    <td>{i + 1}</td>

                    <td>
                      <i
                        className='bi bi-pencil text-primary'
                        style={{ cursor: 'pointer' }}
                        onClick={() => onHandleEdit(x.id)}
                      />
                    </td>

                    <td>{x.designation || ''}</td>

                    <td>
                      {x.salary !== null && x.salary !== undefined
                        ? Number(x.salary).toFixed(2)
                        : '0.00'}
                    </td>

                    <td>{x.leave_allowed ?? 0}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan='5' className='text-center'>
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </CardBody>
      </Card>
    </>
  );
};

export default SalaryDefinitionList;